import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Label } from '@/components/ui-kit/label';
import { Textarea } from '@/components/ui-kit/textarea';
import { useToast } from '@/hooks/use-toast';
import { buildFormFingerprint, FormDefinition, FormRunner } from '@/modules/shared/hr-form-engine';
import {
  formatDate,
  parseFormSnapshot,
  PmFormSnapshot,
} from '@/modules/performance/hr-review-core';
import { ProjectPicker } from '../../components/project-picker/project-picker';
import { useGetSubmission, useSubmitReview } from '../../hooks/use-submissions';
import { useSubmissionDraft } from '../../hooks/use-submission-draft';
import { FormPageSkeleton, PageHeader } from '@/components/core';
import { APP_PATHS, MY_REVIEWS_TABS } from '@/constant/app-paths';

const DRAFT_SAVE_DEBOUNCE_MS = 500;

export const SubmissionAnswerPage = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { pmReviewDetailId } = useParams<{ pmReviewDetailId: string }>();

  const { data: submission, isLoading } = useGetSubmission(pmReviewDetailId);
  const detail = submission?.detail;

  const parsed = useMemo(() => parseFormSnapshot(detail?.PmFormJson), [detail?.PmFormJson]);
  const {
    load: loadDraft,
    save: saveDraft,
    clear: clearDraft,
  } = useSubmissionDraft(pmReviewDetailId);

  const [snapshot, setSnapshot] = useState<PmFormSnapshot | null>(null);
  const [definition, setDefinition] = useState<FormDefinition | null>(null);
  const [projectIds, setProjectIds] = useState<string[]>([]);
  const [comment, setComment] = useState('');
  const initializedFor = useRef<string | null>(null);

  const isCompleted = detail?.Status === 'Completed';
  const isAnnualSelf = detail?.AssessmentType === 'arf' && detail?.ReviewType === 'self';

  // Initialise once per submission: stored answers first, then any newer local draft.
  useEffect(() => {
    if (!detail || !parsed || initializedFor.current === detail.ItemId) return;
    initializedFor.current = detail.ItemId;

    const fingerprint = buildFormFingerprint(
      parsed.definition,
      detail.ItemId,
      detail.LastUpdatedDate
    );
    const draft = isCompleted ? null : loadDraft(fingerprint, parsed.definition);

    setSnapshot(parsed.snapshot);
    setDefinition(draft?.definition ?? parsed.definition);
    setProjectIds(draft?.projectIds ?? detail.ProjectIds ?? []);
    setComment(draft?.comment ?? detail.UpdateComment ?? '');
  }, [detail, parsed, isCompleted, loadDraft]);

  // Autosave while the reviewer types (never for a completed, read-only submission).
  useEffect(() => {
    if (!detail || !definition || isCompleted || initializedFor.current !== detail.ItemId)
      return undefined;
    const timer = setTimeout(
      () =>
        saveDraft({
          fingerprint: buildFormFingerprint(definition, detail.ItemId, detail.LastUpdatedDate),
          definition,
          projectIds,
          comment,
        }),
      DRAFT_SAVE_DEBOUNCE_MS
    );
    return () => clearTimeout(timer);
  }, [detail, definition, projectIds, comment, isCompleted, saveDraft]);

  const { mutateAsync: submitReview, isPending } = useSubmitReview();

  const handleSubmit = async () => {
    if (!detail || !snapshot || !definition) return;
    if (isAnnualSelf && projectIds.length === 0) {
      toast({ title: t('PROJECT_SELECTION_REQUIRED'), variant: 'destructive' });
      return;
    }
    try {
      await submitReview({ detail, snapshot, definition, projectIds, comment });
      clearDraft();
      navigate(`${APP_PATHS.MY_REVIEWS}?tab=${MY_REVIEWS_TABS.SUBMISSIONS}`);
    } catch {
      // surfaced by the mutation's error toast
    }
  };

  if (isLoading) return <FormPageSkeleton />;
  if (!detail || !parsed) {
    return <p className="text-sm text-muted-foreground">{t('REVIEW_NOT_FOUND')}</p>;
  }
  if (!definition || !snapshot) return <FormPageSkeleton />;

  const header = (
    <div className="space-y-4">
      {isAnnualSelf && (
        <ProjectPicker value={projectIds} onChange={setProjectIds} readOnly={isCompleted} />
      )}
      <div className="space-y-1.5">
        <Label htmlFor="review-comment">{t('OVERALL_COMMENT')}</Label>
        <Textarea
          id="review-comment"
          rows={3}
          disabled={isCompleted}
          value={comment}
          onChange={(event) => setComment(event.target.value)}
        />
      </div>
    </div>
  );

  return (
    <div className="flex w-full flex-col gap-6">
      <div>
        <PageHeader title={snapshot.Title || t('REVIEW')} />
        <p className="text-sm text-muted-foreground">
          {detail.Name}
          {detail.PeersDeadLine ? ` · ${t('DEADLINE')}: ${formatDate(detail.PeersDeadLine)}` : ''}
        </p>
      </div>

      <FormRunner
        value={definition}
        onChange={setDefinition}
        onSubmit={handleSubmit}
        readOnly={isCompleted}
        isSubmitting={isPending}
        header={header}
      />
    </div>
  );
};
